import { jsPDF } from 'jspdf';
import { TaskStatus, TaskStatusColors, TaskStatusLabels, GanttRow, GanttExportConfig, FlatExportRow } from '../../types';
import { GANTT_PDF_DIMENSIONS } from '../../constants';

// ===================== Helpers =====================

function hexToRgb(hex: string): [number, number, number] {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

function getGroupSpan(
  row: GanttRow,
  filter: TaskStatus | 'ALL',
): { start?: string; end?: string; count: number } {
  if (row.type === 'task') {
    if (!row.startDate || !row.endDate) return { count: 0 };
    if (filter !== 'ALL' && row.status !== filter) return { count: 0 };
    return { start: row.startDate, end: row.endDate, count: 1 };
  }
  let earliest: string | undefined;
  let latest: string | undefined;
  let count = 0;
  for (const child of row.children || []) {
    const sub = getGroupSpan(child, filter);
    count += sub.count;
    if (sub.start && (!earliest || sub.start < earliest)) earliest = sub.start;
    if (sub.end && (!latest || sub.end > latest)) latest = sub.end;
  }
  return { start: earliest, end: latest, count };
}

/** Flatten a zone tree — fully expanded, respecting status filter */
function flattenForExport(
  root: GanttRow,
  filter: TaskStatus | 'ALL',
): FlatExportRow[] {
  const result: FlatExportRow[] = [];
  function walk(r: GanttRow) {
    if (r.type === 'task') {
      if (filter !== 'ALL' && r.status !== filter) return;
      if (!r.startDate || !r.endDate) return;
      result.push({ row: r, childTaskCount: 1 });
      return;
    }
    const span = getGroupSpan(r, filter);
    if (span.count === 0) return;
    result.push({
      row: r,
      spanStart: span.start,
      spanEnd: span.end,
      childTaskCount: span.count,
    });
    for (const child of r.children || []) walk(child);
  }
  walk(root);
  return result;
}

interface TimelineInfo {
  start: Date;
  days: Date[];
  months: { label: string; days: number; startIndex: number }[];
  totalDays: number;
}

function buildTimeline(start: Date, end: Date): TimelineInfo {
  const days: Date[] = [];
  const cur = new Date(start);
  while (cur <= end) {
    days.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  const months: TimelineInfo['months'] = [];
  let prevMonth = '';
  days.forEach((d, i) => {
    const lbl = d.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    });
    if (lbl !== prevMonth) {
      months.push({ label: lbl, days: 1, startIndex: i });
      prevMonth = lbl;
    } else {
      months[months.length - 1].days++;
    }
  });
  return { start, days, months, totalDays: days.length };
}

function barPos(
  startDate: string,
  endDate: string,
  tlStart: Date,
  totalDays: number,
  dayW: number,
): { left: number; width: number } | null {
  const ts = new Date(startDate + 'T00:00:00');
  const te = new Date(endDate + 'T00:00:00');
  const s = Math.max(
    0,
    Math.floor((ts.getTime() - tlStart.getTime()) / 86400000),
  );
  const e = Math.min(
    totalDays - 1,
    Math.floor((te.getTime() - tlStart.getTime()) / 86400000),
  );
  if (e < 0 || s >= totalDays) return null;
  const dur = Math.max(1, e - s + 1);
  return { left: s * dayW, width: dur * dayW - 0.4 };
}

function truncText(pdf: jsPDF, text: string, maxW: number): string {
  if (pdf.getTextWidth(text) <= maxW) return text;
  let t = text;
  while (t.length > 0 && pdf.getTextWidth(t + '…') > maxW) t = t.slice(0, -1);
  return t.length > 0 ? t + '…' : '';
}

function fmtDate(d: Date): string {
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// ===================== Main Export =====================

export function exportGanttToPdf(config: GanttExportConfig): void {
  const { rows, projectStartDate, projectEndDate, statusFilter, getUserName } =
    config;

  // ---- Layout constants (mm) ----
  const {
    MARGIN: M,
    TASK_LIST_WIDTH: taskListW,
    TASK_ROW_HEIGHT: taskRowH,
    GROUP_ROW_HEIGHT: groupRowH,
    MONTH_ROW_HEIGHT: monthRowH,
    DAY_ROW_HEIGHT: dayRowH,
    ZONE_HEADER_HEIGHT: zoneHeaderH,
    PAGE_HEIGHT: pageH,
    COVER_WIDTH: coverW,
    MAX_DAY_WIDTH: maxDayW,
    MIN_DAY_WIDTH: minDayW,
  } = GANTT_PDF_DIMENSIONS;
  const targetTlW = coverW - M * 2 - taskListW;

  let startStr = projectStartDate;
  let endStr = projectEndDate;

  // Compute date range from tasks if project dates are missing
  if (!startStr || !endStr || isNaN(new Date(startStr).getTime()) || isNaN(new Date(endStr).getTime())) {
    let earliest = '';
    let latest = '';
    const scanDates = (r: GanttRow) => {
      if (r.type === 'task') {
        if (r.startDate && (!earliest || r.startDate < earliest)) earliest = r.startDate;
        if (r.endDate && (!latest || r.endDate > latest)) latest = r.endDate;
      }
      (r.children || []).forEach(scanDates);
    };
    rows.forEach(scanDates);
    if (!startStr || isNaN(new Date(startStr).getTime())) startStr = earliest || new Date().toISOString().slice(0, 10);
    if (!endStr || isNaN(new Date(endStr).getTime())) endStr = latest || startStr;
  }

  const projStart = new Date(startStr + 'T00:00:00');
  const projEnd = new Date(endStr + 'T00:00:00');

  // ---- Project stats ----
  let totalTasks = 0;
  function countTasks(r: GanttRow) {
    if (
      r.type === 'task' &&
      r.startDate &&
      r.endDate &&
      (statusFilter === 'ALL' || r.status === statusFilter)
    )
      totalTasks++;
    (r.children || []).forEach(countTasks);
  }
  rows.forEach(countTasks);

  const zones = rows; // top-level rows

  // ---- Create PDF ----
  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: [coverW, pageH],
  });

  // ============================================================
  //  COVER PAGE
  // ============================================================
  // Dark header stripe
  pdf.setFillColor(44, 62, 80);
  pdf.rect(0, 0, coverW, 44, 'F');

  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(22);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Gantt Chart', M, 20);

  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Project Schedule', M, 30);

  pdf.setFontSize(8);
  pdf.setTextColor(200, 210, 220);
  pdf.text(
    `Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`,
    M,
    38,
  );

  let cy = 54;
  pdf.setTextColor(50, 50, 55);
  pdf.setFontSize(10);
  pdf.text(
    `Period:  ${projectStartDate}  →  ${projectEndDate}`,
    M,
    cy,
  );
  cy += 7;
  pdf.text(`Total Tasks:  ${totalTasks}`, M, cy);
  cy += 7;
  pdf.text(
    `Filter:  ${statusFilter === 'ALL' ? 'All Statuses' : TaskStatusLabels[statusFilter]}`,
    M,
    cy,
  );
  cy += 14;

  // ---- Legend ----
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  pdf.text('Legend', M, cy);
  cy += 7;
  pdf.setFont('helvetica', 'normal');

  let lx = M;
  for (const st of Object.keys(TaskStatusLabels) as TaskStatus[]) {
    const [r, g, b] = hexToRgb(TaskStatusColors[st]);
    pdf.setFillColor(r, g, b);
    pdf.roundedRect(lx, cy - 3, 10, 4.5, 1, 1, 'F');
    pdf.setTextColor(50, 50, 55);
    pdf.setFontSize(8);
    pdf.text(TaskStatusLabels[st], lx + 12, cy);
    lx += 45;
  }
  cy += 14;

  // ---- Zone index ----
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  pdf.setTextColor(50, 50, 55);
  pdf.text('Sections', M, cy);
  cy += 8;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);

  zones.forEach((zone) => {
    const span = getGroupSpan(zone, statusFilter);
    if (span.count === 0) return;
    pdf.setFillColor(44, 62, 80);
    pdf.circle(M + 1.5, cy - 1.2, 1, 'F');
    pdf.setTextColor(50, 50, 55);
    pdf.setFontSize(9);
    pdf.text(
      `${zone.code} — ${zone.name}  (${span.count} tasks)`,
      M + 5,
      cy,
    );
    if (span.start && span.end) {
      cy += 4.5;
      pdf.setFontSize(7.5);
      pdf.setTextColor(130, 130, 130);
      pdf.text(`${span.start}  →  ${span.end}`, M + 5, cy);
    }
    cy += 7;
  });

  // Page number
  pdf.setFontSize(7);
  pdf.setTextColor(160, 160, 160);
  pdf.text('Page 1', coverW - M - 15, pageH - M);

  // ============================================================
  //  ZONE PAGES
  // ============================================================
  let currentPage = 1;

  zones.forEach((zone) => {
    const allFlat = flattenForExport(zone, statusFilter);
    if (allFlat.length === 0) return;

    const span = getGroupSpan(zone, statusFilter);
    if (!span.start || !span.end) return;

    // Zone rows — skip the zone row itself (it becomes the header)
    const zoneRows = allFlat.slice(1);
    if (zoneRows.length === 0) return;

    // Timeline range: pad to month boundaries
    const rawStart = new Date(span.start + 'T00:00:00');
    const rawEnd = new Date(span.end + 'T00:00:00');
    const paddedStart = new Date(rawStart.getFullYear(), rawStart.getMonth(), 1);
    const paddedEnd = new Date(
      rawEnd.getFullYear(),
      rawEnd.getMonth() + 1,
      0,
    );
    const effStart = new Date(
      Math.max(paddedStart.getTime(), projStart.getTime()),
    );
    const effEnd = new Date(
      Math.min(paddedEnd.getTime(), projEnd.getTime()),
    );

    const tl = buildTimeline(effStart, effEnd);

    // Day width
    let dayW = targetTlW / tl.totalDays;
    if (dayW > maxDayW) dayW = maxDayW;
    if (dayW < minDayW) dayW = minDayW;
    const showDayNums = dayW >= 3;

    const tlWidth = tl.totalDays * dayW;
    const pageW = Math.max(coverW, M * 2 + taskListW + tlWidth);

    // ---- Vertically paginate rows ----
    let rIdx = 0;
    let isFirst = true;

    while (rIdx < zoneRows.length) {
      currentPage++;
      pdf.addPage([pageW, pageH]);
      let y = M;
      const tlX = M + taskListW;

      // ==== Zone header (or continuation) ====
      if (isFirst) {
        pdf.setFillColor(44, 62, 80);
        pdf.rect(M, y, pageW - M * 2, zoneHeaderH, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        pdf.text(`${zone.code} — ${zone.name}`, M + 4, y + 5);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.text(
          `${span.count} tasks  ·  ${fmtDate(effStart)} → ${fmtDate(effEnd)}`,
          M + 4,
          y + 10,
        );
        y += zoneHeaderH + 1;
        isFirst = false;
      } else {
        pdf.setFontSize(9);
        pdf.setTextColor(120, 120, 120);
        pdf.setFont('helvetica', 'italic');
        pdf.text(`${zone.code} — ${zone.name} (cont.)`, M, y + 4);
        pdf.setFont('helvetica', 'normal');
        y += 7;
      }

      const chartTop = y;

      // ==== Month header row ====
      pdf.setFillColor(240, 242, 245);
      pdf.rect(M, y, taskListW, monthRowH, 'F');
      pdf.setDrawColor(190, 195, 200);
      pdf.setLineWidth(0.2);

      // "TASK" label
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(7);
      pdf.setTextColor(80, 80, 85);
      pdf.text('TASK', M + 3, y + monthRowH / 2 + 1);

      // Month cells
      tl.months.forEach((m) => {
        const mx = tlX + m.startIndex * dayW;
        const mw = m.days * dayW;
        pdf.setFillColor(240, 242, 245);
        pdf.rect(mx, y, mw, monthRowH, 'F');
        pdf.setDrawColor(185, 188, 195);
        pdf.line(mx, y, mx, y + monthRowH);

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(6.5);
        pdf.setTextColor(55, 58, 65);
        const tw = pdf.getTextWidth(m.label);
        if (tw < mw - 2) {
          pdf.text(m.label, mx + (mw - tw) / 2, y + monthRowH / 2 + 1);
        } else if (mw > 8) {
          const short = m.label.slice(0, 3);
          pdf.text(short, mx + 1, y + monthRowH / 2 + 1);
        }
      });

      // Header borders
      pdf.setDrawColor(180, 183, 190);
      pdf.line(M, y, M + taskListW + tlWidth, y);
      pdf.line(M, y + monthRowH, M + taskListW + tlWidth, y + monthRowH);
      y += monthRowH;

      // ==== Day row ====
      if (showDayNums) {
        pdf.setFillColor(248, 249, 250);
        pdf.rect(M, y, taskListW + tlWidth, dayRowH, 'F');

        tl.days.forEach((d, i) => {
          const dx = tlX + i * dayW;
          const isWe = d.getDay() === 0 || d.getDay() === 6;
          if (isWe) {
            pdf.setFillColor(235, 236, 242);
            pdf.rect(dx, y, dayW, dayRowH, 'F');
          }
          pdf.setDrawColor(228, 230, 235);
          pdf.line(dx, y, dx, y + dayRowH);
          pdf.setFontSize(4.5);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(isWe ? 165 : 105, isWe ? 165 : 105, isWe ? 170 : 110);
          const dn = d.getDate().toString();
          const dtw = pdf.getTextWidth(dn);
          pdf.text(dn, dx + (dayW - dtw) / 2, y + dayRowH / 2 + 1);
        });

        pdf.setDrawColor(180, 183, 190);
        pdf.line(M, y + dayRowH, M + taskListW + tlWidth, y + dayRowH);
        y += dayRowH;
      } else {
        // Week markers when days are too small
        const wkH = 4;
        pdf.setFillColor(248, 249, 250);
        pdf.rect(M, y, taskListW + tlWidth, wkH, 'F');
        tl.days.forEach((d, i) => {
          if (d.getDay() === 1) {
            const dx = tlX + i * dayW;
            pdf.setDrawColor(200, 203, 210);
            pdf.line(dx, y, dx, y + wkH);
            if (dayW * 7 >= 12) {
              pdf.setFontSize(3.5);
              pdf.setTextColor(140, 140, 145);
              pdf.text(`${d.getDate()}`, dx + 0.5, y + 3);
            }
          }
        });
        pdf.setDrawColor(180, 183, 190);
        pdf.line(M, y + wkH, M + taskListW + tlWidth, y + wkH);
        y += wkH;
      }

      const maxBodyY = pageH - M - 8;

      // ==== Draw rows ====
      while (rIdx < zoneRows.length) {
        const fr = zoneRows[rIdx];
        const isTask = fr.row.type === 'task';
        const rh = isTask ? taskRowH : groupRowH;

        if (y + rh > maxBodyY) break; // next page

        // Row background
        if (rIdx % 2 === 0) {
          pdf.setFillColor(253, 253, 255);
        } else {
          pdf.setFillColor(255, 255, 255);
        }
        pdf.rect(M, y, taskListW + tlWidth, rh, 'F');

        // Weekend shading
        const rowY = y;
        tl.days.forEach((d, i) => {
          if (d.getDay() === 0 || d.getDay() === 6) {
            pdf.setFillColor(244, 244, 249);
            pdf.rect(tlX + i * dayW, rowY, dayW, rh, 'F');
          }
        });

        // Row bottom separator
        pdf.setDrawColor(232, 234, 240);
        pdf.setLineWidth(0.15);
        pdf.line(M, y + rh, M + taskListW + tlWidth, y + rh);

        const indent = Math.max(0, fr.row.level - 1) * 4;

        if (isTask) {
          // ---- Task row ----
          // Code
          pdf.setFontSize(5.5);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(85, 85, 95);
          pdf.text(fr.row.code, M + 2 + indent, y + rh / 2 + 1);

          // Name
          const codeW = pdf.getTextWidth(fr.row.code);
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(5.5);
          pdf.setTextColor(45, 48, 55);
          const nameMaxW = taskListW - indent - codeW - 8;
          const name = truncText(pdf, fr.row.name, nameMaxW);
          pdf.text(name, M + 2 + indent + codeW + 2, y + rh / 2 + 1);

          // Task bar
          if (fr.row.startDate && fr.row.endDate && fr.row.status) {
            const bp = barPos(
              fr.row.startDate,
              fr.row.endDate,
              tl.start,
              tl.totalDays,
              dayW,
            );
            if (bp) {
              const [cr, cg, cb] = hexToRgb(TaskStatusColors[fr.row.status]);
              pdf.setFillColor(cr, cg, cb);
              const barY = y + 1.2;
              const barH = rh - 2.4;
              pdf.roundedRect(
                tlX + bp.left,
                barY,
                bp.width,
                barH,
                0.8,
                0.8,
                'F',
              );

              // Bar label (task code)
              pdf.setFontSize(4);
              pdf.setFont('helvetica', 'bold');
              pdf.setTextColor(255, 255, 255);
              const btxt = fr.row.code;
              const btw = pdf.getTextWidth(btxt);
              if (btw < bp.width - 1.5) {
                pdf.text(
                  btxt,
                  tlX + bp.left + 1,
                  barY + barH / 2 + 0.8,
                );
              }

              // Assignee initials
              if (
                fr.row.assignedTo &&
                fr.row.assignedTo.length > 0 &&
                bp.width > btw + 10
              ) {
                const initials = fr.row.assignedTo
                  .map((id) => {
                    const n = getUserName(id);
                    return n
                      ? n
                          .split(' ')
                          .map((w) => w[0])
                          .join('')
                      : '?';
                  })
                  .join(', ');
                pdf.setFontSize(3.5);
                const iw = pdf.getTextWidth(initials);
                if (btw + iw + 3 < bp.width - 1) {
                  pdf.text(
                    initials,
                    tlX + bp.left + bp.width - iw - 1,
                    barY + barH / 2 + 0.8,
                  );
                }
              }
            }
          }
        } else {
          // ---- Group row (section / element) ----
          const isSec = fr.row.type === 'section';

          // Group row background tint
          pdf.setFillColor(
            isSec ? 232 : 240,
            isSec ? 236 : 243,
            isSec ? 248 : 250,
          );
          pdf.rect(M, y, taskListW, rh, 'F');

          // Group label
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(isSec ? 6.5 : 6);
          pdf.setTextColor(
            isSec ? 38 : 65,
            isSec ? 42 : 70,
            isSec ? 58 : 82,
          );
          const groupLabel = `${fr.row.code} — ${fr.row.name}`;
          const maxGrpW = taskListW - indent - 18;
          const trLabel = truncText(pdf, groupLabel, maxGrpW);
          pdf.text(trLabel, M + 2 + indent, y + rh / 2 + 1);

          // Save text width at current font size before changing
          const trLabelW = pdf.getTextWidth(trLabel);

          // Count badge
          pdf.setFontSize(5);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(135, 135, 145);
          pdf.text(
            `(${fr.childTaskCount})`,
            M + 2 + indent + trLabelW + 1.5,
            y + rh / 2 + 1,
          );

          // Group span bar
          if (fr.spanStart && fr.spanEnd) {
            const bp = barPos(
              fr.spanStart,
              fr.spanEnd,
              tl.start,
              tl.totalDays,
              dayW,
            );
            if (bp) {
              const clr = isSec ? [65, 95, 145] : [110, 130, 165];
              pdf.setFillColor(clr[0], clr[1], clr[2]);
              const barMidY = y + rh / 2;
              pdf.rect(tlX + bp.left, barMidY - 1, bp.width, 2.2, 'F');

              // Start/end triangles
              pdf.setFillColor(clr[0], clr[1], clr[2]);
              const sz = 1.3;
              // Left ▶
              pdf.triangle(
                tlX + bp.left,
                barMidY - sz,
                tlX + bp.left + sz,
                barMidY,
                tlX + bp.left,
                barMidY + sz,
                'F',
              );
              // Right ◀
              const rx = tlX + bp.left + bp.width;
              pdf.triangle(
                rx,
                barMidY - sz,
                rx - sz,
                barMidY,
                rx,
                barMidY + sz,
                'F',
              );
            }
          }
        }

        y += rh;
        rIdx++;
      }

      // ==== Task list right border ====
      pdf.setDrawColor(175, 180, 188);
      pdf.setLineWidth(0.3);
      pdf.line(M + taskListW, chartTop, M + taskListW, y);

      // ==== Today marker ====
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const todayIdx = Math.floor(
        (now.getTime() - tl.start.getTime()) / 86400000,
      );
      if (todayIdx >= 0 && todayIdx < tl.totalDays) {
        const tx = tlX + todayIdx * dayW + dayW / 2;
        pdf.setDrawColor(239, 68, 68);
        pdf.setLineWidth(0.4);
        pdf.line(tx, chartTop, tx, y);
        // "Today" label
        pdf.setFontSize(4);
        pdf.setTextColor(239, 68, 68);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Today', tx - 4, chartTop - 1);
        pdf.setFont('helvetica', 'normal');
      }

      // ==== Outer border ====
      pdf.setDrawColor(175, 180, 188);
      pdf.setLineWidth(0.3);
      pdf.rect(M, chartTop, taskListW + tlWidth, y - chartTop, 'S');
      pdf.setLineWidth(0.2);

      // ==== Page number ====
      pdf.setFontSize(7);
      pdf.setTextColor(160, 160, 160);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Page ${currentPage}`, pageW - M - 15, pageH - M);
    }
  });

  pdf.save('gantt-chart.pdf');
}
